import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  getIndirectTaxFilingV2LiabilityReport,
  IIndirectTaxFilingV2LiabilityReportFiling,
} from '@commenda/api/indirect-tax/filings';

import { IndirectTaxFilingAccessHelper } from '../../common/helpers/IndirectTaxFilingAccessHelper';
import { IndirectTaxRegionHelper } from '../../common/helpers/IndirectTaxRegionHelper';
import { useIndirectTaxFilingAccess } from '../../common/hooks/useIndirectTaxFilingAccess';
import { IndirectTaxRegion } from '../../common/types';
import {
  IndirectTaxFilingReturnSummaryHelper,
  IndirectTaxFilingsHelper,
  IndirectTaxLiabilityReportHelper,
} from '../helpers';

import {
  type BreakdownLiabilityReportTableRow,
  type ContributionLiabilityReportTableRow,
  type IIndirectTaxFiling,
} from '../types';

import { BREAKDOWN_LIABILITY_REPORT_COLUMN_GROUPS } from './breakdownLiabilityReportColumnGroups';
import { CONTRIBUTION_LIABILITY_REPORT_COLUMN_GROUPS } from './contributionLiabilityReportColumnGroups';
import { createContributionTabExpandableRowConfig } from './contributionTabExpandableRowConfig';
import { IndirectTaxTaxLiabilityReportTab } from './indirectTaxTaxLiabilityReportTab';

/** Maps Radix Tabs string values to our enum — unknown strings fall back to the default tab. */
function liabilityReportTabFromControlValue(
  value: string
): IndirectTaxTaxLiabilityReportTab {
  if (value === IndirectTaxTaxLiabilityReportTab.UnitedStatesAndCanada) {
    return IndirectTaxTaxLiabilityReportTab.UnitedStatesAndCanada;
  }
  if (
    value === IndirectTaxTaxLiabilityReportTab.EuropeanUnionAndUnitedKingdom
  ) {
    return IndirectTaxTaxLiabilityReportTab.EuropeanUnionAndUnitedKingdom;
  }
  return IndirectTaxTaxLiabilityReportTab.UnitedStatesAndCanada;
}

export interface UseIndirectTaxTaxLiabilityReportModalParams {
  isOpen: boolean;
  corporationId: number;
  filings: IIndirectTaxFiling[];
  periodLabel: string;
}

export function useIndirectTaxTaxLiabilityReportModal({
  isOpen,
  corporationId,
  filings,
  periodLabel,
}: UseIndirectTaxTaxLiabilityReportModalParams) {
  const { isInternalUser } = useIndirectTaxFilingAccess(
    IndirectTaxRegion.ALL_REGIONS
  );
  const [activeLiabilityReportTab, setActiveLiabilityReportTab] =
    useState<IndirectTaxTaxLiabilityReportTab>(
      IndirectTaxTaxLiabilityReportTab.UnitedStatesAndCanada
    );
  const [loadState, setLoadState] = useState<
    'idle' | 'loading' | 'error' | 'success'
  >('idle');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [liabilityFilings, setLiabilityFilings] = useState<
    IIndirectTaxFilingV2LiabilityReportFiling[]
  >([]);

  useEffect(() => {
    if (!isOpen) {
      setLoadState('idle');
      setLoadError(null);
      setLiabilityFilings([]);
      setActiveLiabilityReportTab(
        IndirectTaxTaxLiabilityReportTab.UnitedStatesAndCanada
      );
      return undefined;
    }

    let isCancelled = false;

    const fetchLiabilityReport = async () => {
      setLoadState('loading');
      setLoadError(null);

      const filingIds = filings
        .filter(
          (filing) =>
            IndirectTaxRegionHelper.supportsFilingWorkflow(filing.region) &&
            IndirectTaxFilingAccessHelper.canViewDetails(
              filing.region,
              isInternalUser
            )
        )
        .map((filing) => filing.id);

      try {
        if (filingIds.length === 0) {
          if (!isCancelled) {
            setLiabilityFilings([]);
            setLoadState('success');
          }
          return;
        }

        const report = await getIndirectTaxFilingV2LiabilityReport({
          params: { corporationId, filingIds },
        });

        if (isCancelled) return;

        if (!report.success) {
          setLoadState('error');
          setLoadError(report.error ?? 'Failed to load tax liability report');
          return;
        }

        const loadedFilings = (report.data?.filings ?? []).filter((filing) =>
          filingIds.includes(filing.filingId)
        );
        setLiabilityFilings(loadedFilings);
        setLoadState('success');

        const hasBreakdownData = loadedFilings.some(
          (filing) => filing.breakdown != null
        );
        const hasContributionData = loadedFilings.some(
          (filing) => filing.contributions != null
        );
        if (!hasBreakdownData && hasContributionData) {
          setActiveLiabilityReportTab(
            IndirectTaxTaxLiabilityReportTab.EuropeanUnionAndUnitedKingdom
          );
        } else if (hasBreakdownData && !hasContributionData) {
          setActiveLiabilityReportTab(
            IndirectTaxTaxLiabilityReportTab.UnitedStatesAndCanada
          );
        }
      } catch (error) {
        if (isCancelled) return;
        setLoadState('error');
        setLoadError(
          error instanceof Error
            ? error.message
            : 'Failed to load tax liability report'
        );
      }
    };

    fetchLiabilityReport().catch(() => undefined);

    return () => {
      isCancelled = true;
    };
  }, [isOpen, corporationId, filings, isInternalUser]);

  const currencyByFilingId = useMemo(() => {
    const map = new Map<string, string>();
    filings.forEach((filing) => {
      map.set(filing.id, filing.currency);
    });
    return map;
  }, [filings]);

  const filingsWithBreakdown = useMemo(() => {
    const sorted = liabilityFilings.filter(
      (filing) => filing.breakdown !== null
    );
    sorted.sort((left, right) =>
      IndirectTaxLiabilityReportHelper.formatLiabilityJurisdictionDisplay(
        left.jurisdiction
      ).localeCompare(
        IndirectTaxLiabilityReportHelper.formatLiabilityJurisdictionDisplay(
          right.jurisdiction
        )
      )
    );
    return sorted;
  }, [liabilityFilings]);

  const filingsWithContributions = useMemo(() => {
    const sorted = liabilityFilings.filter(
      (filing) => filing.contributions !== null
    );
    sorted.sort((left, right) =>
      IndirectTaxLiabilityReportHelper.formatLiabilityJurisdictionDisplay(
        left.jurisdiction
      ).localeCompare(
        IndirectTaxLiabilityReportHelper.formatLiabilityJurisdictionDisplay(
          right.jurisdiction
        )
      )
    );
    return sorted;
  }, [liabilityFilings]);

  const breakdownTableRows: BreakdownLiabilityReportTableRow[] = useMemo(
    () =>
      filingsWithBreakdown.map((filing) => {
        const breakdown = filing.breakdown!;
        const currency = currencyByFilingId.get(filing.filingId) ?? '';
        return {
          filingId: filing.filingId,
          jurisdiction:
            IndirectTaxLiabilityReportHelper.formatLiabilityJurisdictionDisplay(
              filing.jurisdiction
            ),
          formTitle: filing.formTitle,
          filingPeriodLabel:
            IndirectTaxLiabilityReportHelper.formatTaxPeriodRange(
              filing.taxPeriodStartDate,
              filing.taxPeriodEndDate
            ),
          dueDateLabel: IndirectTaxFilingsHelper.formatDueDate(filing.dueDate),
          transactionsCount: breakdown.transactionsCount,
          taxableAmount: breakdown.estimatedTaxableAmount,
          nonTaxableAmount: breakdown.estimatedNonTaxableAmount,
          taxDue: breakdown.estimatedTaxCollected,
          filingStatusUI:
            IndirectTaxLiabilityReportHelper.mapLiabilityReportFilingStatusForUI(
              filing.filingStatus
            ),
          currency,
        };
      }),
    [currencyByFilingId, filingsWithBreakdown]
  );

  const contributionTableRows: ContributionLiabilityReportTableRow[] = useMemo(
    () =>
      filingsWithContributions.map((filing) => {
        const currency = currencyByFilingId.get(filing.filingId) ?? '';
        const amountDue =
          IndirectTaxFilingReturnSummaryHelper.computeVatAmountFromContributions(
            filing.contributions ?? []
          ) ?? 0;
        return {
          filingId: filing.filingId,
          jurisdiction:
            IndirectTaxLiabilityReportHelper.formatLiabilityJurisdictionDisplay(
              filing.jurisdiction
            ),
          formTitle: filing.formTitle,
          taxPeriodLabel: IndirectTaxLiabilityReportHelper.formatTaxPeriodRange(
            filing.taxPeriodStartDate,
            filing.taxPeriodEndDate
          ),
          dueDateLabel: IndirectTaxFilingsHelper.formatDueDate(filing.dueDate),
          filingStatusUI:
            IndirectTaxLiabilityReportHelper.mapLiabilityReportFilingStatusForUI(
              filing.filingStatus
            ),
          amountDue,
          currency,
          contributions: filing.contributions,
        };
      }),
    [currencyByFilingId, filingsWithContributions]
  );

  const aggregateSummaryForActiveLiabilityReportTab = useMemo(() => {
    const amounts =
      activeLiabilityReportTab ===
      IndirectTaxTaxLiabilityReportTab.UnitedStatesAndCanada
        ? breakdownTableRows.map(({ currency, taxDue }) => ({
            currency,
            amount: taxDue,
          }))
        : contributionTableRows.map(({ currency, amountDue }) => ({
            currency,
            amount: amountDue,
          }));
    return {
      totalJurisdictions: amounts.length,
      taxDueByCurrency:
        IndirectTaxLiabilityReportHelper.getTaxDueByCurrency(amounts),
    };
  }, [activeLiabilityReportTab, breakdownTableRows, contributionTableRows]);

  const handleExportCSV = useCallback(() => {
    if (
      activeLiabilityReportTab ===
      IndirectTaxTaxLiabilityReportTab.UnitedStatesAndCanada
    ) {
      const csvContent =
        IndirectTaxLiabilityReportHelper.buildBreakdownTabCsvContent(
          filingsWithBreakdown,
          currencyByFilingId
        );
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `tax-liability-united-states-canada-${periodLabel}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
      return;
    }

    const csvContent =
      IndirectTaxLiabilityReportHelper.buildContributionTabCsvContent(
        filingsWithContributions,
        currencyByFilingId
      );
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `tax-liability-european-union-united-kingdom-${periodLabel}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  }, [
    activeLiabilityReportTab,
    currencyByFilingId,
    filingsWithContributions,
    periodLabel,
    filingsWithBreakdown,
  ]);

  const isBreakdownTabScopeEmpty =
    filingsWithBreakdown.length === 0 && loadState === 'success';
  const isContributionTabScopeEmpty =
    filingsWithContributions.length === 0 && loadState === 'success';

  const contributionTabExpandableRowConfig = useMemo(
    () => createContributionTabExpandableRowConfig(),
    []
  );

  return {
    activeLiabilityReportTab,
    setActiveLiabilityReportTab,
    liabilityReportTabFromControlValue,
    loadState,
    loadError,
    filingsWithBreakdown,
    filingsWithContributions,
    breakdownTableRows,
    contributionTableRows,
    aggregateSummaryForActiveLiabilityReportTab,
    isBreakdownTabScopeEmpty,
    isContributionTabScopeEmpty,
    handleExportCSV,
    breakdownColumnGroups: BREAKDOWN_LIABILITY_REPORT_COLUMN_GROUPS,
    contributionColumnGroups: CONTRIBUTION_LIABILITY_REPORT_COLUMN_GROUPS,
    contributionTabExpandableRowConfig,
  };
}
